import React, { useState } from 'react';
import { Card, Table, Avatar, Tag, Typography, Space, Empty, Button, Popconfirm, Badge, Tooltip } from 'antd';
import { UserOutlined, MailOutlined, EyeOutlined, CheckCircleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import dayjs from 'dayjs';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';

const { Title, Text } = Typography;

interface Skill {
  id: number;
  skillName: string;
  skillLevel?: string;
  experienceYears?: number;
  description?: string;
  isApprovedByCurrentMentor: boolean;
  approvalCount: number;
}

interface ApprovedMentee {
  menteeId: number;
  mentee: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
  };
  types: string[];
  type: string;
  firstInteractionDate: string;
  skills: Skill[];
}

const MenteesTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [approvingSkills, setApprovingSkills] = useState<Set<number>>(new Set());

  const { data: mentees = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.mentees,
    queryFn: async () => {
      const res = await apiService.getApprovedMentees();
      if (!res.success) throw new Error(res.message || 'Menteeler yüklenirken hata oluştu.');
      return res.data;
    },
  });

  const approveMutation = useMutation({
    mutationFn: (skillId: number) => apiService.approveMenteeSkill(skillId),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Yetkinlik başarıyla onaylandı.');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.mentees });
      } else toast.error(res.message || 'Yetkinlik onaylanırken hata oluştu.');
    },
    onError: () => toast.error('Yetkinlik onaylanırken hata oluştu.'),
    onSettled: (_, __, skillId) => setApprovingSkills(prev => { const s = new Set(prev); s.delete(skillId); return s; }),
  });

  const removeApprovalMutation = useMutation({
    mutationFn: (skillId: number) => apiService.removeSkillApproval(skillId),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Onay başarıyla kaldırıldı.');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.mentees });
      } else toast.error(res.message || 'Onay kaldırılırken hata oluştu.');
    },
    onError: () => toast.error('Onay kaldırılırken hata oluştu.'),
    onSettled: (_, __, skillId) => setApprovingSkills(prev => { const s = new Set(prev); s.delete(skillId); return s; }),
  });

  const handleApproveSkill = (skillId: number) => {
    setApprovingSkills(prev => new Set(prev).add(skillId));
    approveMutation.mutate(skillId);
  };

  const handleRemoveApproval = (skillId: number) => {
    setApprovingSkills(prev => new Set(prev).add(skillId));
    removeApprovalMutation.mutate(skillId);
  };

  const getSkillLevelColor = (level?: string) => {
    switch (level) {
      case 'Beginner': return 'blue';
      case 'Intermediate': return 'green';
      case 'Advanced': return 'orange';
      case 'Expert': return 'red';
      default: return 'default';
    }
  };

  const getSkillLevelLabel = (level?: string) => {
    const labels: Record<string, string> = {
      'Beginner': 'Başlangıç',
      'Intermediate': 'Orta',
      'Advanced': 'İleri',
      'Expert': 'Uzman'
    };
    return labels[level || ''] || level || 'Belirtilmemiş';
  };

  const getTypeColor = (type: string) => {
    if (type.includes('1-1')) return 'blue';
    if (type.includes('Bootcamp')) return 'green';
    return 'default';
  };

  const columns = [
    {
      title: 'Profil',
      key: 'profile',
      width: 80,
      render: (_: any, record: ApprovedMentee) => (
        <Avatar 
          size={48} 
          src={record.mentee.profileImage ? `data:image/jpeg;base64,${record.mentee.profileImage}` : undefined}
          icon={<UserOutlined />}
          style={{ backgroundColor: 'var(--primary-500)' }}
        />
      ),
    },
    {
      title: 'Ad Soyad',
      key: 'name',
      render: (_: any, record: ApprovedMentee) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: '15px' }}>
            {record.mentee.firstName} {record.mentee.lastName}
          </div>
          <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: '4px' }}>
            <MailOutlined style={{ marginRight: '4px' }} />
            {record.mentee.email}
          </div>
        </div>
      ),
    },
    {
      title: 'Katılım Türü',
      key: 'type',
      render: (_: any, record: ApprovedMentee) => (
        <Space wrap>
          {record.types.map((type, index) => (
            <Tag key={index} color={getTypeColor(type)}>
              {type}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'İlk İletişim',
      key: 'firstInteraction',
      render: (_: any, record: ApprovedMentee) => (
        <Text type="secondary">
          {dayjs(record.firstInteractionDate).format('DD MMM YYYY')}
        </Text>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: ApprovedMentee) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />}
          onClick={() => navigate(`/mentee/${record.mentee.id}`)}
        >
          Detay
        </Button>
      ),
    },
  ];

  const expandedRowRender = (record: ApprovedMentee) => {
    if (!record.skills || record.skills.length === 0) {
      return (
        <div style={{ padding: '16px', textAlign: 'center' }}>
          <Text type="secondary">Henüz yetkinlik eklenmemiş</Text>
        </div>
      );
    }

    return (
      <div style={{ padding: '16px' }}>
        <Title level={5} style={{ marginBottom: '16px' }}>Yetkinlikler</Title>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {record.skills.map((skill) => (
            <Card 
              key={skill.id} 
              size="small"
              style={{ 
                backgroundColor: skill.isApprovedByCurrentMentor ? '#f6ffed' : '#fafafa',
                borderColor: skill.isApprovedByCurrentMentor ? '#b7eb8f' : '#d9d9d9'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Text strong style={{ fontSize: '16px' }}>{skill.skillName}</Text>
                    {skill.skillLevel && (
                      <Tag color={getSkillLevelColor(skill.skillLevel)}>
                        {getSkillLevelLabel(skill.skillLevel)}
                      </Tag>
                    )}
                    {skill.experienceYears && (
                      <Tag color="default">{skill.experienceYears} yıl</Tag>
                    )}
                    {skill.isApprovedByCurrentMentor && (
                      <Badge count={<CheckCircleOutlined style={{ color: 'var(--success)' }} />}>
                        <Tag color="success" icon={<CheckCircleOutlined />}>
                          Onayladınız
                        </Tag>
                      </Badge>
                    )}
                    {skill.approvalCount > 0 && (
                      <Tooltip title={`${skill.approvalCount} mentör tarafından onaylandı`}>
                        <Tag color="blue">{skill.approvalCount} Onay</Tag>
                      </Tooltip>
                    )}
                  </div>
                  {skill.description && (
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                      {skill.description}
                    </Text>
                  )}
                </div>
                <div style={{ marginLeft: '16px' }}>
                  {skill.isApprovedByCurrentMentor ? (
                    <Popconfirm
                      title="Onayı kaldırmak istediğinizden emin misiniz?"
                      onConfirm={() => handleRemoveApproval(skill.id)}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Button
                        type="default"
                        danger
                        icon={<CloseOutlined />}
                        loading={approvingSkills.has(skill.id)}
                        size="small"
                      >
                        Onayı Kaldır
                      </Button>
                    </Popconfirm>
                  ) : (
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={() => handleApproveSkill(skill.id)}
                      loading={approvingSkills.has(skill.id)}
                      size="small"
                    >
                      Onayla
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </Space>
      </div>
    );
  };

  return (
    <PanelTabCard
      title="Menteelerim"
      description="1-1 görüşme veya bootcamp'e katılımı onaylanmış menteeler"
    >
      <TabListLoading loading={loading}>
      <>
        <Card>
          {mentees.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Henüz onaylanmış mentee'niz bulunmuyor"
              style={{ marginTop: '50px' }}
            />
          ) : (
            <Table
              scroll={{ x: 'max-content' }}
              columns={columns}
              dataSource={mentees}
              rowKey="menteeId"
              loading={loading}
              expandable={{
                expandedRowRender,
                rowExpandable: () => true,
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} mentee`,
              }}
            />
          )}
        </Card>
      </>
      </TabListLoading>
    </PanelTabCard>
  );
};

export default MenteesTab;

